import React, { useId, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { IconAiDocument } from '../../../../shared/icon/icon'
import { agoText, utcText } from '../../../../shared/date/local-time'
import { listFindings } from './ai-tools-api'
import { SEVERITY_FILTERS, UNLINKED_DAY_NOTE, findingRow, listFilters, marksCell, personClientCell, receivedAgo, receivedWindow, severityTone } from './ai-tools-words'
import { CLIENT_SPEC, CONSUMER_SPEC, ClientAppFilter, ConsumerStatusFilters, FilterSelect, LOGIN_SPEC, PersonChip, RECEIVED_SPEC, ReceivedFilter, STATUS_SPEC, choiceSpec, useClientApps, useListFilters } from './ai-tools-filters'
import { MarkableList, useMarkableList } from './markable-list'

// The Findings tab (contract §4.6, §8; R2): every finding of every
// postmortem, newest first, filtered by severity, who processed them and
// their status, and a person (its postmortem's), and searched in the grid's
// box: what happened, who it came from, its severity and category, the tool,
// and each consumer's mark. Its title opens its postmortem's page. Mark…,
// Delete and select mode are markable-list.js (shared with the other tabs);
// this holds what is the findings': filters, columns, links and the grid's
// own queryKey.
//
// consumers, onMarked, onNeedConsumers: as useMarkableList.

const QUERY_KEY = 'findings'
const FAILED = "The findings couldn't be loaded."
const markLabel = row => `finding "${row.titleText}"`
const pageOf = row => `/admin/ai-tools/postmortems/${row.postmortemId}`

const titleCell = cell => {
  const row = cell.data
  if (!row) return ''
  return (
    <span className="ak-name-cell">
      <Link className="fw-semibold text-truncate" to={pageOf(row)} onClick={e => e.stopPropagation()}>{row.titleText}</Link>
      {/* the tool, then what happened: one line, so the list fits a laptop's
          window (owner 2026-10-29); all of it, and the client app, on hover */}
      <span className="sw-cell-sub text-truncate" title={[row.toolText, row.whatText, row.clientApp].filter(Boolean).join(' · ')}>
        {row.toolText && <span className="sw-mono">{row.toolText}</span>}{row.toolText && row.whatText ? ' · ' : ''}{row.whatText}
      </span>
    </span>
  )
}
// severity over its category, so the list fits beside its other columns
const severityCell = cell => {
  const row = cell.data
  if (!row) return ''
  return (
    <span className="ak-name-cell">
      {row.severityText ? <span><Pill tone={severityTone(row.severityText)}>{row.severityText}</Pill></span> : <span className="sw-hint">No severity</span>}
      {row.categoryText && <span className="sw-cell-sub">{row.categoryText}</span>}
    </span>
  )
}

// when its postmortem was received, with when it was written under it
const receivedCell = cell => {
  const row = cell.data
  if (!row || !row.receivedAt) return ''
  return (
    <span className="ak-name-cell">
      <span title={row.unlinked === true ? UNLINKED_DAY_NOTE : `${row.receivedText} · ${utcText(row.receivedAt)}`}>{receivedAgo(row, row.unlinked === true)}</span>
      {row.ts && <span className="sw-cell-sub" title={`${row.whenText} · ${utcText(row.ts)}`}>{`Written ${agoText(row.ts)}`}</span>}
    </span>
  )
}

// The tab's filters in the address; it opens on Needs processing, any time.
const FILTERS = { severity: choiceSpec(SEVERITY_FILTERS), client: CLIENT_SPEC, consumer: CONSUMER_SPEC, status: STATUS_SPEC, login: LOGIN_SPEC, ...RECEIVED_SPEC }

export const FindingsTab = ({ consumers, onMarked, onNeedConsumers }) => {
  const ids = { severity: useId(), client: useId() }
  const clientApps = useClientApps()
  const { filters: f, update } = useListFilters(FILTERS)
  const { severity, client, consumer, status, login } = f

  const received = useMemo(() => receivedWindow(f), [f])
  const { from, to, problem } = received

  const filters = useMemo(() => ({ ...listFilters({ consumer, status, login, client }), severity, from, to }), [consumer, status, login, client, severity, from, to])
  const list = useMarkableList({ fetchPage: listFindings, toRow: findingRow, filters, failedText: FAILED, kind: 'finding', labelOf: markLabel, consumers, onMarked, onNeedConsumers, blocked: problem, recycleTab: 'findings' })
  const set = key => list.change(value => update({ [key]: value }))

  const controls = (
    <>
      <FilterSelect id={ids.severity} label="Severity" value={severity} onChange={set('severity')} options={SEVERITY_FILTERS} />
      <ClientAppFilter id={ids.client} value={client} onChange={set('client')} apps={clientApps} />
      <ConsumerStatusFilters consumers={consumers} consumer={consumer} status={status} onConsumer={set('consumer')} onStatus={set('status')} />
      <ReceivedFilter value={f} onChange={list.change(update)} />
      <PersonChip login={login} person={list.personOf(login)} onClear={() => set('login')('')} />
    </>
  )

  return (
    <MarkableList list={list} queryKey={QUERY_KEY} title="Findings" controls={controls} rowTarget={pageOf}
      emptyState={{ Icon: IconAiDocument, title: 'No findings', message: 'No finding matches these filters.' }}>
      <ColumnFormat field="titleText" header="Finding" sortable={false} flex={2} textMax={220} stacked render={titleCell} />
      <ColumnFormat field="personText" header="Person" sortable={false} textMax={170} stacked render={personClientCell} />
      <ColumnFormat field="severityText" header="Severity" sortable={false} fit="content" stacked render={severityCell} />
      <ColumnFormat field="receivedText" header="Received" sortable={false} fit="content" stacked render={receivedCell} />
      <ColumnFormat field="marksText" header="Marks" sortable={false} flex={1.2} render={marksCell} />
    </MarkableList>
  )
}
